/**
 * SessionTimeoutWarningModal Component
 * Displays a modal warning when the user's session is about to expire
 * Only used in the OATH app - respects user's session-timeout-warning setting
 */

import { useState, useEffect, useCallback } from 'react';
import { ClockIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { OathModal, OathButton, OathDualSpinner } from './OathUI';
import { useOathAuth } from '../../services/oathAuthContext';
import { useOathSettings } from '../../services/oathSettingsContext';
import { SettingKeys } from '../../services/oathUserSettingsService';

/**
 * SessionTimeoutWarningModal - Shows a warning modal before session expires
 * The warning threshold is controlled by the user's session-timeout-warning setting
 */
const SessionTimeoutWarningModal = () => {
  const { sessionTimeRemaining, refreshSession, logout } = useOathAuth();
  const { getIntegerSetting, isLoading: settingsLoading } = useOathSettings();

  const [isOpen, setIsOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [warningShown, setWarningShown] = useState(false);

  // Get the user's warning threshold setting (in minutes, 0 = disabled)
  const warningMinutes = getIntegerSetting(SettingKeys.SESSION_TIMEOUT_WARNING);

  // Convert remaining time to minutes
  const minutesRemaining = Math.floor(sessionTimeRemaining / 60000);
  const secondsRemaining = Math.floor((sessionTimeRemaining % 60000) / 1000);

  // Check if we should show the warning
  useEffect(() => {
    // Don't show if settings are still loading
    if (settingsLoading) return;

    // Don't show if warning is disabled (0 minutes)
    if (warningMinutes === 0) return;

    // Don't show if session has expired
    if (sessionTimeRemaining <= 0) {
      setIsOpen(false);
      return;
    }

    // Show warning when time remaining crosses the threshold
    if (minutesRemaining <= warningMinutes && !warningShown) {
      setIsOpen(true);
      setWarningShown(true);
    }
  }, [minutesRemaining, warningMinutes, warningShown, sessionTimeRemaining, settingsLoading]);

  // Reset warning shown state when session is refreshed (time goes back up)
  useEffect(() => {
    if (warningMinutes > 0 && minutesRemaining > warningMinutes) {
      setWarningShown(false);
    }
  }, [minutesRemaining, warningMinutes]);

  // Handle extending the session
  const handleExtendSession = useCallback(async () => {
    setIsRefreshing(true);
    const result = await refreshSession();
    setIsRefreshing(false);

    if (result.success) {
      setIsOpen(false);
      setWarningShown(false); // Allow warning to show again if needed
    }
  }, [refreshSession]);

  // Handle logout
  const handleLogout = useCallback(() => {
    setIsOpen(false);
    logout();
  }, [logout]);

  // Handle closing the modal (just dismisses, doesn't extend)
  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Format time remaining for display
  const formatTimeRemaining = () => {
    if (minutesRemaining > 0) {
      return `${minutesRemaining} minute${minutesRemaining !== 1 ? 's' : ''} and ${secondsRemaining} second${secondsRemaining !== 1 ? 's' : ''}`;
    }
    return `${secondsRemaining} second${secondsRemaining !== 1 ? 's' : ''}`;
  };

  // Get urgency level for styling
  const getUrgencyLevel = () => {
    if (minutesRemaining <= 1) return 'critical';
    if (minutesRemaining <= 5) return 'warning';
    return 'normal';
  };

  const urgency = getUrgencyLevel();

  // Don't render if warning is disabled
  if (warningMinutes === 0) return null;

  return (
    <OathModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Session Expiring Soon"
      size="sm"
      footer={
        <div className="flex flex-col sm:flex-row gap-3 w-full">
          <OathButton
            variant="outline"
            onClick={handleLogout}
            disabled={isRefreshing}
            className="flex-1 sm:flex-none"
          >
            Sign Out
          </OathButton>
          <OathButton
            variant="primary"
            onClick={handleExtendSession}
            loading={isRefreshing}
            icon={ArrowPathIcon}
            className="flex-1 sm:flex-none"
          >
            Extend Session
          </OathButton>
        </div>
      }
    >
      <div className="text-center">
        {/* Animated clock icon */}
        <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
          urgency === 'critical'
            ? 'bg-red-100 dark:bg-red-900/30'
            : urgency === 'warning'
            ? 'bg-orange-100 dark:bg-orange-900/30'
            : 'bg-primary-100 dark:bg-primary-900/30'
        }`}>
          {isRefreshing ? (
            <OathDualSpinner size="md" />
          ) : (
            <ClockIcon className={`h-8 w-8 ${
              urgency === 'critical'
                ? 'text-red-600 dark:text-red-400'
                : urgency === 'warning'
                ? 'text-orange-600 dark:text-orange-400'
                : 'text-primary dark:text-primary-light'
            }`} />
          )}
        </div>

        {/* Warning message */}
        <p className="text-gray-700 dark:text-gray-300 mb-2">
          Your session will expire in
        </p>

        {/* Time remaining - updates live */}
        <p className={`text-2xl font-bold mb-4 ${
          urgency === 'critical'
            ? 'text-red-600 dark:text-red-400'
            : urgency === 'warning'
            ? 'text-orange-600 dark:text-orange-400'
            : 'text-primary dark:text-primary-light'
        }`}>
          {formatTimeRemaining()}
        </p>

        {/* Help text */}
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Click "Extend Session" to continue working, or "Sign Out" to end your session now.
        </p>
      </div>
    </OathModal>
  );
};

export default SessionTimeoutWarningModal;
